import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/layout/ProtectedRoute';
import AppLayout from './components/layout/AppLayout';

// Public Pages (no auth required)
import ApplyPage from './pages/public/ApplyPage';
import ContinueApplicationPage from './pages/public/ContinueApplicationPage';
import SurveyPage from './pages/public/SurveyPage';
import SchedulePage from './pages/public/SchedulePage';
import SetPasswordPage from './pages/public/SetPasswordPage';
import MyPortalPage from './pages/my/MyPortalPage';
import MyOfferLetterPage from './pages/my/MyOfferLetterPage';
import MyPoliciesPage from './pages/my/MyPoliciesPage';
import PoliciesAdminPage from './pages/admin/PoliciesAdminPage';
import MyOnboardingPage from './pages/my/MyOnboardingPage';
import MyDirectDepositPage from './pages/my/MyDirectDepositPage';
import MyW4Page from './pages/my/MyW4Page';
import MyEmergencyContactPage from './pages/my/MyEmergencyContactPage';
import MyDoctorNotesPage from './pages/my/MyDoctorNotesPage';
import DoctorNotesApprovalsPage from './pages/admin/DoctorNotesApprovalsPage';
import MyFilePage from './pages/MyFilePage';
import MyAssessmentPage from './pages/my/MyAssessmentPage';
import MyTimePage from './pages/my/MyTimePage';
import MySchedulePage from './pages/my/MySchedulePage';
import ClockedHoursPage from './pages/admin/ClockedHoursPage';
import MyGoalScorecardPage from './pages/my/MyGoalScorecardPage';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/admin/DashboardPage';
import RecruitmentPage from './pages/admin/RecruitmentPage';
import CandidateDetailPage from './pages/admin/CandidateDetailPage';
import TalentPoolPage from './pages/admin/TalentPoolPage';
import OnboardingPage from './pages/admin/OnboardingPage';
import PendingApprovalsPage from './pages/admin/PendingApprovalsPage';
import CompliancePage from './pages/admin/CompliancePage';
import ObservationsPage from './pages/admin/ObservationsPage';
import OneOnOnesPage from './pages/admin/OneOnOnesPage';
import DisciplinaryPage from './pages/admin/DisciplinaryPage';
import PerformancePage from './pages/admin/PerformancePage';
import TasksPage from './pages/admin/TasksPage';
import MessagesPage from './pages/admin/MessagesPage';
import ContactsPage from './pages/admin/ContactsPage';
import AccountVaultPage from './pages/admin/AccountVaultPage';
import SearchPage from './pages/admin/SearchPage';
import StaffPage from './pages/admin/StaffPage';
import AttendancePage from './pages/admin/AttendancePage';
import StaffAttendanceDetailPage from './pages/admin/StaffAttendanceDetailPage';
import AttendanceRulesPage from './pages/admin/AttendanceRulesPage';
import SettingsPage from './pages/admin/SettingsPage';
import OnboardingTemplatesPage from './pages/admin/OnboardingTemplatesPage';
import WalletSettingsPage from './pages/admin/WalletSettingsPage';
import WalletApprovalsPage from './pages/admin/WalletApprovalsPage';
import FileHealthPage from './pages/admin/FileHealthPage';
import ReportsPage from './pages/admin/ReportsPage';
import ActivityLogPage from './pages/admin/ActivityLogPage';
import OwnerCommandCenterPage from './pages/admin/OwnerCommandCenterPage';
import RatioCompliancePage from './pages/admin/RatioCompliancePage';
import TeamSchedulePage from './pages/admin/TeamSchedulePage';
import TimeOffPage from './pages/admin/TimeOffPage';
import AvailabilityRequestsPage from './pages/admin/AvailabilityRequestsPage';
import ClassroomsPage from './pages/admin/ClassroomsPage';
import EnrollmentForecastPage from './pages/admin/EnrollmentForecastPage';
import GoalTemplatesPage from './pages/admin/GoalTemplatesPage';
import CompensationTiersPage from './pages/admin/CompensationTiersPage';
import HolidaysPage from './pages/admin/HolidaysPage';
import PayRulesPage from './pages/admin/PayRulesPage';
import CalendarPage from './pages/admin/CalendarPage';
import PayrollPage from './pages/admin/PayrollPage';
import GoalScorecardsPage from './pages/admin/GoalScorecardsPage';
import AdminInterviewSlotsPage from './pages/admin/AdminInterviewSlotsPage';
import ClockPage from './pages/ClockPage';
import ClockKioskPage from './pages/ClockKioskPage';
import AdminTimeClockPage from './pages/admin/AdminTimeClockPage';

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: { borderRadius: '12px', padding: '12px 16px', fontSize: '14px' },
            success: { iconTheme: { primary: '#f97316', secondary: '#fff' } },
          }}
        />
        <Routes>
          {/* Public — no auth */}
          <Route path="/apply" element={<ApplyPage />} />
          <Route path="/continue/:token" element={<ContinueApplicationPage />} />
          <Route path="/survey/:token" element={<SurveyPage />} />
          <Route path="/schedule/:token" element={<SchedulePage />} />
          <Route path="/set-password/:token" element={<SetPasswordPage />} />

          {/* Phase 4 candidate "/my/*" surface — auth'd, but no AppLayout sidebar
              since hired-but-not-yet-active staff don't need the admin nav. */}
          <Route path="/my" element={<ProtectedRoute><MyPortalPage /></ProtectedRoute>} />
          <Route path="/my/offer-letter" element={<ProtectedRoute><MyOfferLetterPage /></ProtectedRoute>} />
          {/* RETIRED 2026-09-19. Both screens answered "has the handbook been
              published?" from handbook_versions — a table nothing writes to
              any more — and told a director who had just published six
              documents that none existed. Redirected rather than deleted so
              existing links land on the system of record. */}
          <Route path="/my/handbook" element={<Navigate to="/my/policies" replace />} />
          <Route path="/my/policies" element={<ProtectedRoute><MyPoliciesPage /></ProtectedRoute>} />
          <Route path="/admin/policies" element={<ProtectedRoute requireDirector><PoliciesAdminPage /></ProtectedRoute>} />
          <Route path="/my/onboarding" element={<ProtectedRoute><MyOnboardingPage /></ProtectedRoute>} />
          <Route path="/my/direct-deposit" element={<ProtectedRoute><MyDirectDepositPage /></ProtectedRoute>} />
          <Route path="/my/w4" element={<ProtectedRoute><MyW4Page /></ProtectedRoute>} />
          <Route path="/my/emergency-contact" element={<ProtectedRoute><MyEmergencyContactPage /></ProtectedRoute>} />
          <Route path="/my/doctor-notes" element={<ProtectedRoute><MyDoctorNotesPage /></ProtectedRoute>} />
          <Route path="/my/time" element={<ProtectedRoute><MyTimePage /></ProtectedRoute>} />
          <Route path="/doctor-note-approvals" element={<ProtectedRoute requireManagement><DoctorNotesApprovalsPage /></ProtectedRoute>} />
          <Route path="/login" element={<LoginPage />} />

          {/* Building tablet kiosk — public, no auth. Shows rotating QR
              for staff to scan with their already-authed phone. */}
          <Route path="/kiosk" element={<ClockKioskPage />} />

          {/* Protected routes with sidebar layout */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/recruitment" element={<ProtectedRoute requireManagement><RecruitmentPage /></ProtectedRoute>} />
            <Route path="/recruitment/talent-pool" element={<ProtectedRoute requireManagement><TalentPoolPage /></ProtectedRoute>} />
            <Route path="/recruitment/candidates/:id" element={<ProtectedRoute requireManagement><CandidateDetailPage /></ProtectedRoute>} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/pending-approvals" element={<ProtectedRoute requireManagement><PendingApprovalsPage /></ProtectedRoute>} />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/observations" element={<ObservationsPage />} />
            <Route path="/one-on-ones" element={<OneOnOnesPage />} />
            <Route path="/disciplinary" element={<DisciplinaryPage />} />
            <Route path="/performance" element={<PerformancePage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/messages" element={<ProtectedRoute requireDirector><MessagesPage /></ProtectedRoute>} />
            <Route path="/contacts" element={<ProtectedRoute requireManagement><ContactsPage /></ProtectedRoute>} />
            <Route path="/account-vault" element={<ProtectedRoute requireManagement><AccountVaultPage /></ProtectedRoute>} />
            <Route path="/search" element={<ProtectedRoute requireDirector><SearchPage /></ProtectedRoute>} />
            <Route path="/staff" element={<ProtectedRoute requireManagement><StaffPage /></ProtectedRoute>} />
            <Route path="/attendance" element={<ProtectedRoute requireManagement><AttendancePage /></ProtectedRoute>} />
            <Route path="/attendance/staff/:staffId" element={<ProtectedRoute><StaffAttendanceDetailPage /></ProtectedRoute>} />
            <Route path="/staff/:staffId" element={<ProtectedRoute><StaffAttendanceDetailPage /></ProtectedRoute>} />
            <Route path="/attendance/rules" element={<ProtectedRoute requireDirector><AttendanceRulesPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute requireDirector><SettingsPage /></ProtectedRoute>} />
            <Route path="/settings/onboarding-templates" element={<ProtectedRoute requireDirector><OnboardingTemplatesPage /></ProtectedRoute>} />
            <Route path="/settings/wallet" element={<ProtectedRoute requireDirector><WalletSettingsPage /></ProtectedRoute>} />
            <Route path="/wallet-approvals" element={<ProtectedRoute requireManagement><WalletApprovalsPage /></ProtectedRoute>} />
            <Route path="/file-health" element={<ProtectedRoute requireManagement><FileHealthPage /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute requireDirector><ReportsPage /></ProtectedRoute>} />
            <Route path="/activity" element={<ProtectedRoute requireDirector><ActivityLogPage /></ProtectedRoute>} />
            <Route path="/command-center" element={<ProtectedRoute requireDirector><OwnerCommandCenterPage /></ProtectedRoute>} />
            <Route path="/ratio-compliance" element={<ProtectedRoute requireManagement><RatioCompliancePage /></ProtectedRoute>} />
            <Route path="/team-schedule" element={<ProtectedRoute requireManagement><TeamSchedulePage /></ProtectedRoute>} />
            <Route path="/time-off" element={<ProtectedRoute><TimeOffPage /></ProtectedRoute>} />
            <Route path="/availability-requests" element={<ProtectedRoute requireDirector><AvailabilityRequestsPage /></ProtectedRoute>} />
            <Route path="/goals/templates" element={<ProtectedRoute requireDirector><GoalTemplatesPage /></ProtectedRoute>} />
            <Route path="/goals/comp-tiers" element={<ProtectedRoute requireDirector><CompensationTiersPage /></ProtectedRoute>} />
            <Route path="/holidays" element={<ProtectedRoute requireDirector><HolidaysPage /></ProtectedRoute>} />
            <Route path="/pay-rules" element={<ProtectedRoute requireDirector><PayRulesPage /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
            <Route path="/handbook" element={<Navigate to="/my/policies" replace />} />
            <Route path="/my/file" element={<ProtectedRoute><MyFilePage /></ProtectedRoute>} />
            <Route path="/my/assessment" element={<ProtectedRoute><MyAssessmentPage /></ProtectedRoute>} />
            <Route path="/my/scorecard" element={<ProtectedRoute><MyGoalScorecardPage /></ProtectedRoute>} />
            <Route path="/my/schedule" element={<ProtectedRoute><MySchedulePage /></ProtectedRoute>} />
            <Route path="/clocked-hours" element={<ProtectedRoute requireDirector><ClockedHoursPage /></ProtectedRoute>} />
            <Route path="/payroll" element={<ProtectedRoute requireDirector><PayrollPage /></ProtectedRoute>} />
            <Route path="/classrooms" element={<ProtectedRoute requireDirector><ClassroomsPage /></ProtectedRoute>} />
            <Route path="/enrollment-forecast" element={<ProtectedRoute requireManagement><EnrollmentForecastPage /></ProtectedRoute>} />
            <Route path="/scorecards" element={<ProtectedRoute requireDirector><GoalScorecardsPage /></ProtectedRoute>} />
            <Route path="/interview-slots" element={<ProtectedRoute requireManagement><AdminInterviewSlotsPage /></ProtectedRoute>} />
            <Route path="/clock" element={<ProtectedRoute><ClockPage /></ProtectedRoute>} />
            <Route path="/time-clock" element={<ProtectedRoute requireManagement><AdminTimeClockPage /></ProtectedRoute>} />
          </Route>

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
